import React, { useState, useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ExternalList } from "../../../context/MovingResources";
import { useAppState } from "../../../context/StateContext";
import { Instruction, When, InstructionOptions } from "../../../models/instruction";
import InstructionService from "../../../services/instruction.service";
import { Status } from "../../../util/enums";
import { Select } from "../../Form";
import Button, { Color } from "../../Form/Button";
import LoadingModal from "../../LoadingModal";
import ActionButtons from "../ActionButtons";
import InstructionsList from "./InstructionsList";
import InstructionsModal from "./InstructionsModal";
import useStyles from "./styles";

type Props = {
  onCancel?: () => void;
  onSave?: () => void;
  onSavePublish?: () => void;
  handleUpdate?: (value: Instruction[]) => void;
  onSaveAndSend?: () => void;
  instructions: Instruction[];
  isUpdating?: boolean;
  externalList?: ExternalList;
  isOnViewMode?: boolean;
  isOrder?: boolean;
  isSavingAndSending?: boolean;
};

enum InstructionType {
  UPLIFT = "uplift",
  DELIVERY = "delivery",
}

const Instructions = ({
  onCancel,
  onSave,
  onSavePublish,
  handleUpdate,
  onSaveAndSend,
  instructions,
  isUpdating,
  externalList,
  isOnViewMode = false,
  isOrder = false,
  isSavingAndSending = false,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [when, setWhen] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [instructionsOptions, setInstructionsOptions] = useState<Instruction[]>(
    externalList?.instructionList || []
  );

  const typeWhens: When[] = [
    {
      label: translate("uplift"),
      value: "Uplift",
      icon: { name: "inventory", size: "22px", margin: "3px" },
    },
    {
      label: translate("delivery"),
      value: "Delivery",
      icon: { name: "local_shipping", size: "24px" },
    },
    {
      label: translate("coordinator"),
      value: "Move Coordinator",
      icon: { name: "today", size: "24px" },
    },
  ];

  const { isFetching: loadingInstructionOptions } = useQuery(
    ["instructionOptions.get"],
    () =>
      InstructionService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["description"]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !externalList?.instructionList,
      onSuccess: (response) => {
        if (response && response.data) {
          setInstructionsOptions(response.data);
        }
      },
    }
  );

  const getUniquesWhen = (uplifts: Instruction[], deliveries: Instruction[]) => {
    const whens: Instruction[] = [...uplifts];

    deliveries.forEach((delivery) => {
      let encountered = false;

      uplifts.forEach((uplift) => {
        if (uplift.name === delivery.name) {
          encountered = true;
        }
      });
      if (!encountered) whens.push({ ...delivery });
    });

    return whens;
  };

  const instructionMultiList = useMemo(() => {
    const uplifts = instructionsOptions.filter(
      (instruction) => instruction.when?.toLowerCase() === InstructionType.UPLIFT
    );
    const deliveries = instructionsOptions.filter(
      (instruction) => instruction.when?.toLowerCase() === InstructionType.DELIVERY
    );
    const coordinators = getUniquesWhen(uplifts, deliveries);

    switch (when.toLowerCase()) {
      case InstructionType.UPLIFT:
        return uplifts.map((instruction) => ({
          id: instruction.id || 0,
          name: instruction.description || translate("instructionsNotFound") || "",
        }));
      case InstructionType.DELIVERY:
        return deliveries.map((instruction) => ({
          id: instruction.id || 0,
          name: instruction.description || translate("instructionsNotFound") || "",
        }));
      default:
        return coordinators.map((instruction) => ({
          id: instruction.id || 0,
          name: instruction.description || translate("instructionsNotFound") || "",
        }));
    }
  }, [when]);

  const renderWhens = () => {
    return typeWhens?.map((typeWhen, index) => {
      return (
        <MenuItem key={`${typeWhen.label}-${index}`} value={typeWhen.value}>
          {typeWhen.label}
        </MenuItem>
      );
    });
  };

  const renderInstructionsModal = () => {
    const submitInstructions = (instructionOptions: InstructionOptions[]) => {
      setOpenModal(false);
      const instructionsAux: Instruction[] = [...instructions];

      instructionOptions.forEach((instruction) => {
        const instructionAux: Instruction = {
          name: instruction.name,
          when,
        };

        instructionsAux.push(instructionAux);
      });

      if (handleUpdate) handleUpdate(instructionsAux);
    };

    return (
      <InstructionsModal
        isModalOpen={openModal}
        closeModal={() => setOpenModal(false)}
        instructionOptions={instructionMultiList}
        selectedWhen={when}
        selectedInstructions={instructions}
        submit={submitInstructions}
      />
    );
  };

  const renderInstructionsList = () => {
    const updateSelectedInstruction = (instruction: Instruction) => {
      const selectedInstructionsAux = [...instructions];
      const instructionIndex = selectedInstructionsAux.findIndex(
        (selectedInstruction) =>
          selectedInstruction.name === instruction.name &&
          selectedInstruction.when === instruction.when
      );

      selectedInstructionsAux[instructionIndex].remarks = instruction.remarks;
      if (handleUpdate) handleUpdate(selectedInstructionsAux);
    };

    const removeSelectedInstruction = (instruction: Instruction) => {
      const selectedInstructionsAux = [...instructions];
      const instructionIndex = selectedInstructionsAux.findIndex(
        (selectedInstruction) =>
          selectedInstruction.name === instruction.name &&
          selectedInstruction.when === instruction.when
      );

      if (instructionIndex > -1) selectedInstructionsAux.splice(instructionIndex, 1);
      if (handleUpdate) handleUpdate(selectedInstructionsAux);
    };

    return (
      <InstructionsList
        isOnViewMode={isOnViewMode}
        typeWhens={typeWhens}
        instructions={instructions}
        onUpdateInstruction={updateSelectedInstruction}
        onRemoveInstruction={removeSelectedInstruction}
        isOrder={isOrder}
      />
    );
  };

  const handleAddInstruction = () => setOpenModal(true);

  return (
    <div>
      <div className={isOnViewMode ? classes.containerView : classes.container}>
        {!isOnViewMode && (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-end"
            className={classes.grid}
            spacing={2}
          >
            {loadingInstructionOptions ? (
              <>
                <Grid item xs={12} sm={4}>
                  <Skeleton variant="text" width="100%" height="60px" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Skeleton variant="text" width="100%" height="60px" />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  <Select
                    name="when"
                    value={when}
                    label={translate("when")}
                    onChange={(event: SelectChangeEvent<any>) => setWhen(event.target.value)}
                    data-cy="select-when"
                    displayEmpty
                    placeholder={translate("selectWhen")}
                  >
                    {renderWhens()}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    customColor={Color.SECONDARY}
                    className={`${classes.button} fullWidth`}
                    data-cy="button-add-when"
                    disabled={when === "" || loadingInstructionOptions}
                    onClick={handleAddInstruction}
                  >
                    <AddCircleOutlineIcon className={classes.iconButton} />
                    <strong>{translate("addInstruction")}</strong>
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        )}
        {renderInstructionsModal()}
        {renderInstructionsList()}

        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              showSaveAndSendButton={isSavingAndSending}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onCancel={onCancel}
              onSave={onSave}
              onSavePublish={!isUpdating ? onSavePublish : undefined}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default Instructions;
